import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Duration } from 'luxon'
import { useEffect } from 'react'
import { CAPACITOR_UPDATER_ENABLED, CAPGO_CHANNEL } from '../data/environment'

const AUTOMATIC_UPDATE_CHECK_DURATION = Duration.fromObject({ minutes: 15 })
const BUNDLE_READY_POLL_MS = 2000
const BUNDLE_READY_MAX_POLLS = 30

const IGNORED_ERRORS = [
  'disable_auto_update_under_native',
  'No new version available',
  'no_new_version_available',
  'no_bundle',
  'no_channel',
]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** download() resolves while the bundle may still be unzipping; wait until it is usable. */
const waitForBundleReady = async (id: string): Promise<boolean> => {
  for (let poll = 0; poll < BUNDLE_READY_MAX_POLLS; poll++) {
    const { bundles } = await CapacitorUpdater.list()
    const status = bundles.find((bundle) => bundle.id === id)?.status
    if (status === 'error') return false
    const isReady = status !== undefined && status !== 'downloading'
    if (isReady) return true
    await sleep(BUNDLE_READY_POLL_MS)
  }
  return false
}

/** A copy of `version` already on disk and usable (a copy still downloading must not block). */
const findReadyCopy = async (version: string) => {
  const { bundles } = await CapacitorUpdater.list()
  return bundles.find(
    (bundle) =>
      bundle.version === version &&
      (bundle.status === 'pending' || bundle.status === 'success'),
  )
}

/** Queues the channel's newer bundle, if any, for the next background: reuses a
 * copy already on disk (next() keeps getLatest() reporting the same version until
 * the app backgrounds, so every 15-min tick would re-download it otherwise). */
const downloadAndQueueLatest = async () => {
  const latestUpdate = await CapacitorUpdater.getLatest({
    channel: CAPGO_CHANNEL,
  })
  if (!latestUpdate.url) return

  const readyCopy = await findReadyCopy(latestUpdate.version)
  if (readyCopy) {
    await CapacitorUpdater.next({ id: readyCopy.id })
    return
  }

  const bundle = await CapacitorUpdater.download({
    url: latestUpdate.url,
    version: latestUpdate.version,
  })
  const isReady = await waitForBundleReady(bundle.id)
  if (!isReady) {
    console.error('[CapacitorUpdater] bundle never became ready', bundle.id)
    return
  }
  await CapacitorUpdater.next({ id: bundle.id })
}

const checkForUpdate = async () => {
  try {
    await downloadAndQueueLatest()
  } catch (error) {
    const message = (error as { message?: string } | null)?.message
    const isExpected = !message || IGNORED_ERRORS.includes(message)
    if (!isExpected) console.error('[CapacitorUpdater] check failed', error)
  }
}

/**
 * Checks the Capgo channel (`CAPGO_CHANNEL`) for a newer JS bundle on load and
 * every 15 minutes, downloads it and queues it with `next()`, so it applies the
 * next time the app goes to the background. No-op unless the OTA updater is
 * enabled (native + production).
 *
 * Not `set()`: it reloads immediately and, on Android, holds the new page's
 * `notifyAppReady()` until the ready timeout expires, so Capgo rolls every
 * update back (seen as a white screen, then the old version again).
 */
export const CapacitorUpdateOnLocationChange = () => {
  useEffect(() => {
    if (!CAPACITOR_UPDATER_ENABLED) return
    checkForUpdate()
    const interval = setInterval(
      checkForUpdate,
      AUTOMATIC_UPDATE_CHECK_DURATION.toMillis(),
    )
    return () => clearInterval(interval)
  }, [])

  return null
}
